import React, { useEffect, useState } from 'react';
import { DefaultButton, Label, PrimaryButton, Stack, Text, TextField } from '@fluentui/react';
import IvicosStrings from 'kits/language/stringKit';
import { OrganizationDto } from 'services/client-api-wrapper/identity_provider/responses.dto';
import { DOCUMENSO_CLOUD_URL, IDocumensoConfiguration } from './documenso.types';

interface IDocumensoSettingsFormProps {
    documensoBaseUrl: string;
    isOpen: boolean;
    updateDocumensoConfig: (config: IDocumensoConfiguration) => Promise<OrganizationDto>;
    onSaveSuccess: () => void;
    onCancel: () => void;
}

// A quick client-side check only; the identity-provider is the authority (it also rejects private
// and internal addresses) and answers 400 for anything it will not store.
const looksLikeHttpsUrl = (value: string): boolean => {
    try {
        return new URL(value.trim()).protocol === 'https:';
    } catch {
        return false;
    }
};

// Shared by the save path (here) and DocumensoSettings.tsx's deactivation path.
export function resolveDocumensoErrorMessage(error: unknown): string {
    const status =
        typeof error === 'object' && error !== null && 'response' in error ? (error as { response?: { status?: number } }).response?.status : undefined;
    // 400 = the backend's model validation refused the base URL.
    if (status === 400) return IvicosStrings.documensoSettingsInvalidUrlError;
    return IvicosStrings.somethingWentWrong;
}

export const DocumensoSettingsForm: React.FC<IDocumensoSettingsFormProps> = ({ documensoBaseUrl, isOpen, updateDocumensoConfig, onSaveSuccess, onCancel }) => {
    // A new configuration starts from Documenso's hosted service; most organisations keep it.
    const initialBaseUrl = documensoBaseUrl || DOCUMENSO_CLOUD_URL;
    const [baseUrl, setBaseUrl] = useState(initialBaseUrl);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // The Panel is mounted while closed too, so re-sync once org data arrives.
    useEffect(() => {
        setBaseUrl(initialBaseUrl);
    }, [initialBaseUrl]);

    // Discard unsaved drafts when the panel closes.
    useEffect(() => {
        if (!isOpen) {
            setErrorMessage(null);
            setBaseUrl(initialBaseUrl);
            setIsSaving(false);
        }
    }, [isOpen, initialBaseUrl]);

    const handleSave = async (): Promise<void> => {
        if (!looksLikeHttpsUrl(baseUrl)) {
            setErrorMessage(IvicosStrings.documensoSettingsInvalidUrlError);
            return;
        }
        setIsSaving(true);
        setErrorMessage(null);
        try {
            await updateDocumensoConfig({ is_documenso_enable: true, documenso_base_url: baseUrl.trim() });
            onSaveSuccess();
        } catch (error) {
            console.error('Failed to save Documenso settings:', error);
            setErrorMessage(resolveDocumensoErrorMessage(error));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Stack verticalAlign="space-between" styles={{ root: { height: '100%', minHeight: 'calc(100vh - 110px)' } }}>
            <Stack tokens={{ childrenGap: 16 }} style={{ marginTop: 16 }}>
                <Stack tokens={{ childrenGap: 4 }}>
                    <Label>{IvicosStrings.documensoSettingsBaseUrlLabel}</Label>
                    <TextField
                        type="url"
                        value={baseUrl}
                        placeholder="https://app.documenso.com"
                        description={IvicosStrings.documensoSettingsBaseUrlHint}
                        onChange={(_ev, newValue) => setBaseUrl(newValue ?? '')}
                        data-test-id="documenso-base-url"
                    />
                </Stack>
                {errorMessage && (
                    <Text style={{ color: '#A80000' }} data-test-id="documenso-settings-error">
                        {errorMessage}
                    </Text>
                )}
            </Stack>
            <Stack horizontal tokens={{ childrenGap: 16 }} styles={{ root: { width: '100%', paddingBottom: 8 } }}>
                <Stack.Item grow styles={{ root: { flexBasis: 0 } }}>
                    <DefaultButton
                        text={IvicosStrings.documensoSettingsCancelButton}
                        onClick={onCancel}
                        styles={{ root: { width: '100%' } }}
                        data-test-id="documenso-cancel-button"
                    />
                </Stack.Item>
                <Stack.Item grow styles={{ root: { flexBasis: 0 } }}>
                    <PrimaryButton
                        text={IvicosStrings.documensoSettingsSaveButton}
                        onClick={handleSave}
                        disabled={isSaving || !baseUrl.trim()}
                        styles={{ root: { width: '100%' } }}
                        data-test-id="documenso-save-button"
                    />
                </Stack.Item>
            </Stack>
        </Stack>
    );
};
